import { useState, useEffect } from 'react';

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl?: string;
  isLoading?: boolean;
  error?: string;
}

export default function ShareDialog({
  isOpen,
  onClose,
  shareUrl,
  isLoading = false,
  error,
}: ShareDialogProps) {
  const [copied, setCopied] = useState(false);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Reset copied state when dialog closes
  useEffect(() => {
    if (!isOpen) setCopied(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback silently
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-[620px] p-7 flex flex-col gap-5"
        style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-dialog-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between w-full">
          <h2 id="share-dialog-title" className="text-lg font-semibold text-gray-900">
            分享行程
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-400 hover:bg-gray-200 transition-colors"
            aria-label="關閉"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Subtitle */}
        <p className="text-sm text-gray-500 leading-relaxed">
          複製以下連結，分享給朋友一起規劃旅程！
        </p>

        {/* Error state */}
        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}

        {/* URL Row */}
        <div className="flex items-center gap-2.5 w-full">
          {/* URL Field */}
          <div className="flex items-center gap-2.5 flex-1 h-12 px-3.5 rounded-xl bg-gray-100 border border-gray-200 min-w-0">
            {isLoading ? (
              /* Loading spinner */
              <div className="flex items-center gap-2.5 w-full">
                <svg className="shrink-0 text-gray-400 animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                <span className="text-[13px] text-gray-400">正在產生連結...</span>
              </div>
            ) : (
              <>
                {/* Link icon */}
                <svg className="shrink-0 text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                <span className="text-[13px] text-gray-500 truncate">{shareUrl}</span>
              </>
            )}
          </div>

          {/* Copy Button */}
          <button
            onClick={handleCopy}
            disabled={isLoading || !shareUrl}
            className="shrink-0 flex items-center justify-center w-12 h-12 rounded-xl transition-opacity hover:opacity-90 active:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#00BCD4' }}
            aria-label="複製連結"
            title={copied ? '已複製！' : '複製連結'}
          >
            {copied ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
